/**
 * The canonical FSRS fold — the single convergence primitive (EVENTS.md §3,
 * SDD §2.7, proven in spikes/offline-sync/REPORT.md / FLASH-17).
 *
 * There is intentionally ONE fold implementation. The offline client (FLASH-30)
 * imports the same rule; two implementations are two chances to diverge. Card
 * state is a pure function of the event SET for a card, made deterministic by
 * imposing one total order before folding.
 *
 * Load-bearing facts (do not "optimize" away — each has a regression test):
 *  - The fold is ORDER-SENSITIVE: ts-fsrs next() derives delta_t from the card's
 *    previous review, so state depends on the sequence, not just the set.
 *  - Folding out of time order THROWS (negative delta_t) — ordering is a
 *    correctness precondition, not a nicety.
 *  - Same-ts events need the event_id tie-break or replicas diverge.
 *  - enable_fuzz MUST be false (cross-library determinism).
 *  - The clock-skew clamp must use the CLAMPED effective_ts for BOTH ordering
 *    and folding, and server_recv_ts must be frozen on the event.
 */
import {
  fsrs,
  generatorParameters,
  createEmptyCard,
  State,
  type FSRS,
  type Grade,
} from "ts-fsrs";
import type { CardState, ReviewSource } from "../shared/types";

/** 24h forward clock-skew window (EVENTS.md §4). Named so client and DO agree. */
export const SKEW_WINDOW_MS = 24 * 60 * 60 * 1000;

/** FSRS parameters, param-versioned for retraining (EVENTS.md §7, FR-5.4). */
export interface FsrsParams {
  /** 21-weight vector w0..w20 (FSRS-6). */
  w: number[];
  /** FSRS default 0.9 unless tuned. */
  request_retention: number;
  /** Param version this state was folded under. */
  version: number;
  /** epoch ms. */
  updated_at: number;
}

/**
 * A review event as the fold consumes it: the immutable client fact plus the
 * server-frozen receipt time (EVENTS.md §1.2). server_recv_ts is REQUIRED — it
 * is the upper-bound input to the skew clamp; the clamp is deterministic only
 * because this value is frozen on the event, never read from a live clock.
 */
export interface FoldEvent {
  event_id: string;
  card_id: string;
  ts: number;
  grade: Grade;
  source: ReviewSource;
  server_recv_ts: number;
}

/** An event with its derived fold time attached (EVENTS.md §3.1). */
export type FoldableEvent = FoldEvent & { effective_ts: number };

/**
 * Canonical total order (EVENTS.md §3.1/§3.3). Sorts by effective_ts ascending
 * — the SAME derived value the fold uses, never raw ts (sorting raw but folding
 * clamped runs the fold backward and throws; FLASH-17 findings 6–7) — with
 * tie-break by event_id ascending. event_id is a UUID, so this is a STRICT
 * total order: no two distinct events compare equal, hence the fold result is
 * uniquely determined by the event set. The offline client (FLASH-30) imports
 * this exact comparator (EVENTS.md §9).
 */
export function canonicalCompare(a: FoldableEvent, b: FoldableEvent): number {
  if (a.effective_ts !== b.effective_ts) return a.effective_ts - b.effective_ts;
  return a.event_id < b.event_id ? -1 : a.event_id > b.event_id ? 1 : 0;
}

/**
 * Derived fold time (EVENTS.md §4). Pure function of fields frozen on the event
 * (ts, server_recv_ts) and the card's creation anchor — never a replay-time
 * clock — so it is identical on every replica/replay.
 *
 * Lower bound (creation anchor): a review cannot predate the card; this also
 * prevents the negative-delta_t throw. Upper bound (frozen receipt window): a
 * runaway-future client clock cannot poison the fold. For source:'import',
 * server_recv_ts is import time so the upper bound sits far in the future and is
 * effectively a no-op — historical ts folds as-is (FR-4.3).
 */
export function effectiveTs(e: FoldEvent, cardCreationTs: number): number {
  const upper = e.server_recv_ts + SKEW_WINDOW_MS;
  return Math.min(Math.max(e.ts, cardCreationTs), upper);
}

const DO_STATE: Record<number, CardState> = {
  [State.New]: "new",
  [State.Learning]: "learning",
  [State.Review]: "review",
  [State.Relearning]: "relearning",
};

/** Build a scheduler for one param version. enable_fuzz:false is mandatory. */
export function makeScheduler(params: FsrsParams): FSRS {
  return fsrs(
    generatorParameters({
      w: params.w,
      request_retention: params.request_retention,
      enable_fuzz: false,
    }),
  );
}

/** Folded per-card result (matches card_state columns, SDD §2.2). */
export interface FoldedCard {
  card_id: string;
  stability: number;
  difficulty: number;
  due_ts: number;
  state: CardState;
  reps: number;
  lapses: number;
  last_event_id: string | null;
}

/**
 * Fold one card's events into card_state (EVENTS.md §3.3). `events` may be in
 * any order and may contain duplicate event_ids; we dedupe then impose the
 * canonical order. Sorting and folding BOTH use the effective ts, so the fold
 * sequence is non-decreasing by construction and ts-fsrs never throws on a
 * forward-skewed event (FLASH-17 Finding 6).
 */
export function foldCard(
  scheduler: FSRS,
  cardId: string,
  cardCreationTs: number,
  events: FoldEvent[],
): FoldedCard {
  // Dedupe by event_id (idempotent union), attach the clamped effective ts.
  const byId = new Map<string, FoldableEvent>();
  for (const e of events) {
    if (e.card_id !== cardId) continue;
    byId.set(e.event_id, { ...e, effective_ts: effectiveTs(e, cardCreationTs) });
  }
  // Canonical order on effective_ts (NOT raw ts): a forward-skewed event clamped
  // below its raw-ts predecessor would otherwise fold backward and throw
  // (Finding 6). Sort key == fold time, by construction.
  const ordered = [...byId.values()].sort(canonicalCompare);

  let card = createEmptyCard(new Date(cardCreationTs));
  for (const e of ordered) {
    card = scheduler.next(card, new Date(e.effective_ts), e.grade).card;
  }

  return {
    card_id: cardId,
    stability: card.stability,
    difficulty: card.difficulty,
    due_ts: card.due.getTime(),
    state: DO_STATE[card.state] ?? "new",
    reps: card.reps,
    lapses: card.lapses,
    last_event_id: ordered.at(-1)?.event_id ?? null,
  };
}
